/**
 * SuccessStorySection — section 7 of the homepage (Konzept § 5 Z. 132).
 *
 * Job: prove by example — *one* customer, in the pattern Aufgabe → Ergebnis →
 * Zitat, exiting to the full story.
 *
 * Deliberately not SuccessStoriesSection: that one is a carousel built to rotate
 * several stories, and § 5 asks for the opposite ("Belegen am Einzelfall"). A
 * carousel of one is a carousel with a bug; this renders the single case flat.
 *
 * The quote carries an author and role because Konzept § 7 rules out anonymous
 * testimonials — an unattributed quote would undercut the very claim it makes.
 */

import React from "react";
import { type SuccessStoryProps } from "../../content/section-success-stories";
import { useLocalizedUrl } from "../../hooks";
import { ResponsiveImage, SectionTextLink } from "../ui";

export type SuccessStorySectionProps = {
    title?: string;
    story: SuccessStoryProps;
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (12.8.2026)
     *
     * Im Beweis-Kapitel der Startseite (`ChapterSection`) steht die
     * Kapitelueberschrift bereits als `h2`. «Erfolgsgeschichten» faellt deshalb
     * auf `h3` und der Fallname darunter auf `h4` — sonst stuenden zwei `h2`
     * im selben Kapitel und die Gliederung laese sich fuer einen Screenreader
     * als zwei Abschnitte, genau der Eindruck, den die Fusion aufloest.
     *
     * Flaeche und Aussenabstand entfallen ebenfalls; beides setzt das Kapitel.
     */
    nested?: boolean;
};

export const SuccessStorySection: React.FC<SuccessStorySectionProps> = ({ title, story, nested = false }) => {
    const localizedHref = useLocalizedUrl(story.href);

    // Nur die Ebene wechselt, nicht die Rolle: `SectionHeading` ist immer die
    // Ueberschrift der Sektion, `StoryHeading` immer der Name des Falls.
    const SectionHeading = nested ? "h3" : "h2";
    const StoryHeading = nested ? "h4" : "h3";

    return (
        <section data-testid="success-story-section" className={nested ? "" : "bg-white py-24"}>
            <div className="mx-auto w-full px-4 md:px-12">
                {/* Nur der Sektionstitel zentriert (Jan, 6.8.2026) — er steht allein
                    ueber dem Inhalt, wie die Titel der Nachbarsektionen. Die
                    Erfolgsgeschichte darunter bleibt linksbuendig: sie sitzt im
                    festen Zweispalten-Raster neben ihrer Abbildung, und zentrierter
                    Text neben einem Bild laese sich schlechter. */}
                {title && (
                    <SectionHeading
                        className={`mb-12 text-center font-black text-gray-900 ${
                            nested ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"
                        }`}
                    >
                        {title}
                    </SectionHeading>
                )}
                <div className="grid items-center gap-12 lg:grid-cols-2">
                    <div>
                        <StoryHeading className="mb-1 text-2xl font-black text-gray-900">{story.title}</StoryHeading>
                        {story.subtitle && <p className="mb-6 text-gray-500">{story.subtitle}</p>}
                        <p className="mb-8 text-lg leading-relaxed text-gray-600">{story.description}</p>

                        {story.quote && (
                            <blockquote
                                data-testid="success-story-quote"
                                className="mb-8 border-l-4 border-gray-900 pl-6"
                            >
                                <p className="mb-3 text-lg italic leading-relaxed text-gray-900">«{story.quote}»</p>
                                {story.quoteAuthor && (
                                    <footer className="text-sm text-gray-500">
                                        {story.quoteAuthor}
                                        {story.quoteAuthorRole && <>, {story.quoteAuthorRole}</>}
                                    </footer>
                                )}
                            </blockquote>
                        )}

                        {/*
                            Dieselbe Gestalt wie die uebrigen Sektions-Ausgaenge
                            (Jan, 3.8.2026). Vorher: fetter, unterstrichener
                            Grautext — dritte Variante fuer dieselbe Handlung.
                        */}
                        {story.cta && <SectionTextLink to={localizedHref}>{story.cta}</SectionTextLink>}
                    </div>
                    <div>
                        <ResponsiveImage
                            src={story.image}
                            alt={story.title}
                            className="h-auto w-full rounded-2xl"
                            widths={[640, 960, 1280]}
                            sizes="(max-width: 1024px) 100vw, 50vw"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SuccessStorySection;
